import type { BadgeTone } from '@/components/admin/entretiens/status-badge';
import type { ChatRole, ChatRoomAdminState } from '@matter_extractor/shared';

/**
 * What the two interview screens SAY (chat spec §12, issue #11; écrans S9 / S10
 * de la maquette).
 *
 * Pure, and the only address of these sentences: S9 and S10 render the same room
 * at the same moment - the table's badge and the transcript's head tile - so two
 * spellings of « invité parti » would be two facts about one interview. Régime
 * of `status-badge.tsx`, which the coupe moved beside it (issue #32): both sit
 * under `components/admin/entretiens/` rather than in `lib/` because they serve
 * exactly these two screens.
 *
 * The tone tables are `Record`s on the enums of `shared`: a fifth state added to
 * the schema without its wording CEASES TO COMPILE rather than falling through a
 * default and being painted grey - the one tone that says « rien à signaler ».
 */

export const SCREEN_TITLE = 'Entretiens à l’aveugle';
export const SCREEN_SUBTITLE =
  'Chaque salle porte deux liens, valables 24 heures. Les transcripts restent ici après expiration.';
export const CREATE_ROOM_LABEL = 'Nouvelle salle';

/**
 * The notice S9 carries above its table, and the reason the screen has one at
 * all (§12).
 *
 * The two links are readable at the creation and NOWHERE else - there is no
 * route that could serve them again - so an operator who closes the panel
 * without copying has destroyed a room. Saying so BEFORE the click, in the page
 * rather than only in the panel, is what makes the panel's own warning arrive as
 * a reminder instead of as news.
 */
export const LINKS_ONCE_NOTICE =
  'Les deux liens ne s’affichent qu’à la création de la salle. Copiez-les avant de fermer la fenêtre, puis envoyez-en un à chaque partie.';

/** The columns of S9, in the order the maquette draws them. */
export const ROOM_COLUMNS = ['Référence', 'Créée', 'État', 'Messages', 'Durée'] as const;

/** What a row's last cell offers, and what it says when there is nothing to open. */
export const OPEN_TRANSCRIPT_LABEL = 'Ouvrir le transcript';
export const NO_TRANSCRIPT_LABEL = 'Aucun transcript';

/**
 * The four states, capitalised for a badge (§12).
 *
 * « Jamais ouverte » and « Invité parti » are the two the spec makes an
 * obligation of: they are how one spots an interview that did not happen, and a
 * table showing « 0 message » tells neither of them.
 */
export const ROOM_STATE_LABEL: Record<ChatRoomAdminState, string> = {
  'never-opened': 'Jamais ouverte',
  chatting: 'En cours',
  closed: 'Terminé',
  abandoned: 'Invité parti',
};

/**
 * The tones of the maquette, mapped onto the tokens of the charte.
 *
 * Vert for an interview being held right now, bleu-nuit for one that finished,
 * jaune-deep for the guest who left - a finding about the conduct, and exactly
 * what the cycle wants to be able to read (§11) - and grey for a room nobody
 * entered, which is the absence of an event rather than an incident. Rouge
 * nowhere: nothing on this screen is an irreversible action.
 */
export const ROOM_STATE_TONE: Record<ChatRoomAdminState, BadgeTone> = {
  'never-opened': 'neutral',
  chatting: 'success',
  closed: 'accent',
  abandoned: 'warning',
};

/** The role, as an operator reads it. Never « guest » on a French screen. */
export const ROLE_LABEL: Record<ChatRole, string> = {
  interviewer: 'Interviewer',
  guest: 'Invité',
};

/** What a dead link says beside a reference, and what it does NOT say. */
export const LINK_EXPIRED_LABEL = 'Lien expiré';
export const LINK_EXPIRED_TITLE =
  'Les deux liens ne résolvent plus. Le transcript, lui, est conservé.';

/** The head of S10, and the sentence that says what its order means. */
export const BACK_TO_ROOMS_LABEL = 'Tous les entretiens';
export const COPY_MARKDOWN_LABEL = 'Copier en markdown';
export const COPY_MARKDOWN_DONE = 'Copié';
export const COPY_MARKDOWN_FAILED = 'La copie n’a pas abouti. Réessayez.';
export const TRANSCRIPT_HEADING = 'Transcript';
export const ARRIVAL_ORDER_NOTE = 'Ordre d’arrivée, jamais l’ordre de rédaction';

/** The five head tiles of S10. */
export const TILE_STATE = 'État';
export const TILE_MESSAGES = 'Messages';
export const TILE_DURATION = 'Durée';
export const TILE_CONSENTS = 'Consentements';
export const TILE_BREAKS = 'Coupures';

/** What the thread says when a room holds nothing. A result, and not a failure. */
export const EMPTY_TRANSCRIPT_LABEL =
  'Aucun message : personne n’a écrit dans cette salle. C’est l’un des deux entretiens qui n’ont pas eu lieu.';

/** What the listing says when it is empty, and when a search found nothing. */
export const EMPTY_ROOMS_LABEL = 'Aucune salle. Frappez-en une pour obtenir ses deux liens.';
export const NO_MATCHING_ROOM_LABEL =
  'Aucune salle sous cette référence. Vérifiez-la auprès de la personne qui l’a citée.';

/** The erasure search of §12. */
export const SEARCH_LABEL = 'Référence citée';
export const SEARCH_PLACEHOLDER = '7K4M-92XB';
export const SEARCH_HINT =
  'La référence que l’invité cite pour demander un effacement. Huit symboles et un tiret.';
export const SEARCH_MALFORMED =
  'Cette référence n’a pas la forme attendue (7K4M-92XB). Retypez-la.';
export const SEARCH_SUBMIT = 'Chercher';
export const SEARCH_CLEAR = 'Tout afficher';

/**
 * A creation date, short: `02/09 21:02`.
 *
 * Day and month without the year, and the maquette is right about it: an
 * interview is read within days of being held, and a four-digit year in every
 * row of a five-column table costs width the reference needs. Local time on
 * purpose - the operator reads the hour she held the conversation at.
 *
 * An unparseable stamp prints itself back rather than « Invalid Date »: it is a
 * stored value, and showing it is what lets somebody diagnose it.
 */
export function createdAtLabel(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * The hour a message ARRIVED, to the minute: `21:02`.
 *
 * To the minute and not to the second, because the reading this screen exists
 * for is a RHYTHM - « en combien de tours la première matière arrive, à quel
 * rythme il monte en profondeur ». Seconds would put six characters of noise in
 * front of every line of that reading, and the one place a finer grain would
 * matter - a message that waited in the queue - is served by the gap, not by the
 * stamp.
 */
export function timeLabel(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** How long a traced break lasted. Rounded UP: « 0 min » would deny the cut. */
export function breakLabel(lateByMs: number): string {
  return `Coupure de connexion, ${Math.max(1, Math.round(lateByMs / 60_000))} min`;
}

/**
 * The two hours of a late message (§8.2).
 *
 * BOTH, in one sentence, because the gap is itself the information: the person
 * validated at one hour and the server received it at another, and rendering
 * only the second would hide that the thread's order is not the order she wrote
 * in. It is the one place `composedAt` earns its place on a screen.
 */
export function lateStampsLabel(composedAt: string, receivedAt: string): string {
  return `Composé à ${timeLabel(composedAt)}, arrivé à ${timeLabel(receivedAt)}`;
}

/** A message the queue delivered after the closure (§11), marked and not slipped in. */
export const AFTER_CLOSURE_LABEL = 'Arrivé après la clôture';

/** The two links of a freshly struck room, and what the panel says about them. */
export const CREATED_PANEL_TITLE = 'Salle frappée';
export const CREATED_PANEL_WARNING =
  'C’est la seule fois où ces deux liens sont lisibles. Copiez-les maintenant : aucun écran ne pourra les réafficher.';
export const INTERVIEWER_LINK_LABEL = 'Lien de l’interviewer';
export const GUEST_LINK_LABEL = 'Lien de l’invité';
export const COPY_LINK_LABEL = 'Copier';
export const COPY_LINK_DONE = 'Copié';
export const CREATED_PANEL_DISMISS = 'J’ai copié les deux liens';
export const CREATE_FAILED = 'La salle n’a pas pu être frappée. Réessayez.';

/**
 * The link an operator distributes, built from the token.
 *
 * Absolute, from the ORIGIN the operator is on: the two links are pasted into a
 * message somewhere else, so a relative path would be useless, and hardcoding
 * the production host would hand out a dead link from every dev stack. The path
 * is `/chat/<jeton>` - one TanStack route on the SPA already served at the root
 * (§3), which is why there is no prefix to configure here.
 */
export function chatLinkFor(origin: string, token: string): string {
  return `${origin}/chat/${token}`;
}
